import styled from "@emotion/styled";
import { DEFAULT_COUNTRY_CODE, PhoneCountryDropdown } from "@ui/components/inputs/components/phone-country-dropdown";
import { colors } from "@ui/style/colors";
import {
	AsYouType,
	CountryCode,
	formatIncompletePhoneNumber,
	getCountryCallingCode,
	getExampleNumber,
	isSupportedCountry,
	validatePhoneNumberLength,
} from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";
import React, { InputHTMLAttributes, useCallback, useEffect, useMemo, useState } from "react";

interface PhoneInputProps extends InputHTMLAttributes<HTMLInputElement> {
	error?: boolean;
	initialCountryCode: string;
}

export const PhoneNumberInput = ({ error = false, onChange, initialCountryCode }: PhoneInputProps): JSX.Element => {
	const [phoneNumber, setPhoneNumber] = useState("");
	const [countryCode, setCountryCode] = useState<string>(DEFAULT_COUNTRY_CODE);

	useEffect(() => {
		if (phoneNumber === "") {
			const givenCountryCode = initialCountryCode.toUpperCase();
			if (isSupportedCountry(givenCountryCode)) {
				setCountryCode(givenCountryCode);
			} else {
				setCountryCode(DEFAULT_COUNTRY_CODE);
			}
		}
	}, [initialCountryCode, phoneNumber]);

	const { countryCallingCode, phonePlaceholder } = useMemo(() => {
		const callingCode = "+" + getCountryCallingCode(countryCode as CountryCode) + " ";
		const placeholder = getExampleNumber(countryCode as CountryCode, examples)
			?.formatInternational()
			.substring(callingCode.length)
			.replace(/\d/g, "0");
		return { countryCallingCode: callingCode, phonePlaceholder: placeholder };
	}, [countryCode]);

	const handlePhoneNumberChanged = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			// Convert value to E.164 to avoid leading zeros
			const asYouType = new AsYouType(countryCode as CountryCode);
			asYouType.input(countryCallingCode + event.target.value);
			const e164 = asYouType.getNumber()?.format("E.164") ?? countryCallingCode;

			let formattedPhoneNumber = formatIncompletePhoneNumber(e164, countryCode as CountryCode);
			if (validatePhoneNumberLength(formattedPhoneNumber) === "TOO_LONG") {
				return;
			}

			event.target.value = formattedPhoneNumber;
			onChange?.(event);

			formattedPhoneNumber = formattedPhoneNumber.substring(countryCallingCode.length);
			setPhoneNumber(formattedPhoneNumber);
		},
		[countryCallingCode, countryCode, onChange],
	);

	return (
		<InputContainer error={error}>
			<PhoneCountryDropdown
				value={countryCode}
				onChange={value => {
					setCountryCode(value);
					setPhoneNumber("");
				}}
			/>
			<div>{countryCallingCode}</div>
			<StyledInput
				className="p-size-s"
				placeholder={phonePlaceholder}
				value={phoneNumber}
				onChange={handlePhoneNumberChanged}
			/>
		</InputContainer>
	);
};

const InputContainer = styled.div<{ error: boolean }>`
	display: flex;
	align-items: center;
	background-color: ${colors.primaryBackground};
	font-size: 1rem;
	color: ${colors.black};
	border: 1px solid;
	border-radius: 0.5rem;
	transition: border-color 0.2s;
	border-color: ${({ error }) => (error ? colors.rejection : "transparent")};

	:hover,
	:focus-within {
		border-color: ${({ error }) => (error ? colors.rejection : colors.primary)};
	}
`;

const StyledInput = styled.input`
	padding: 1em 1.5em;
	padding-left: 0.5em;
	width: 100%;
	background-color: unset;
	border: none;

	:not(.p-disabled) {
		box-shadow: none !important;
	}

	:focus-visible {
		outline: none;
	}
`;
